"use client";

import {
  businessFiltersAtom,
  type RangeFilter as RangeFilterType,
} from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import {
  RangeFilter,
  type FilterRangeValue,
} from "@/components/common/range-filter";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterLabel } from "@/app/(saas)/(app)/businesses/_components/filters/filter-label";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import type { SaleTypeType } from "@repo/database";

const SALE_TYPE_OPTIONS: SaleTypeType[] = ["ASSET_SALE", "STOCK_SALE", "HYBRID"];

const NON_LOCALIZED_ESTABLISHMENT_FILTERS = [
  {
    stringValue: "_null",
    value: {
      min: null,
      max: null,
    },
  },

  {
    stringValue: "under_2_years",
    value: {
      min: null,
      max: 2,
    },
  },

  {
    stringValue: "2_to_5_years",
    value: {
      min: 2,
      max: 5,
    },
  },

  {
    stringValue: "6_to_10_years",
    value: {
      min: 6,
      max: 10,
    },
  },

  {
    stringValue: "11_to_20_years",
    value: {
      min: 11,
      max: 20,
    },
  },

  {
    stringValue: "over_20_years",
    value: {
      min: 20,
      max: null,
    },
  },
] as const satisfies Omit<FilterRangeValue, "label">[];

const NON_LOCALIZED_EMPLOYEES_FILTERS = [
  {
    stringValue: "_null",
    value: {
      min: null,
      max: null,
    },
  },

  {
    stringValue: "1_to_5",
    value: {
      min: 1,
      max: 5,
    },
  },

  {
    stringValue: "6_to_10",
    value: {
      min: 6,
      max: 10,
    },
  },

  {
    stringValue: "11_to_20",
    value: {
      min: 11,
      max: 20,
    },
  },

  {
    stringValue: "21_to_50",
    value: {
      min: 21,
      max: 50,
    },
  },

  {
    stringValue: "51_to_100",
    value: {
      min: 51,
      max: 100,
    },
  },

  {
    stringValue: "101_to_200",
    value: {
      min: 101,
      max: 200,
    },
  },

  {
    stringValue: "over_200",
    value: {
      min: 200,
      max: null,
    },
  },
] as const satisfies Omit<FilterRangeValue, "label">[];

const NON_LOCALIZED_OWNER_WORKED_HOURS_FILTERS = [
  {
    stringValue: "_null",
    value: {
      min: null,
      max: null,
    },
  },

  {
    stringValue: "under_10",
    value: {
      min: null,
      max: 10,
    },
  },

  {
    stringValue: "10_to_20",
    value: {
      min: 10,
      max: 20,
    },
  },

  {
    stringValue: "20_to_30",
    value: {
      min: 20,
      max: 30,
    },
  },

  {
    stringValue: "30_to_40",
    value: {
      min: 30,
      max: 40,
    },
  },

  {
    stringValue: "over_40",
    value: {
      min: 40,
      max: null,
    },
  },
] as const satisfies Omit<FilterRangeValue, "label">[];

export function DetailsFilter() {
  const t = useTranslations();

  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const facets = useListingFacetCounts();

  const ESTABLISHMENT_FILTERS = useMemo<FilterRangeValue[]>(() => {
    return NON_LOCALIZED_ESTABLISHMENT_FILTERS.map((data) => ({
      ...data,
      label: t(
        `saas.search.filters.fields.details.establishment.values.${data.stringValue}`,
      ),
    }));
  }, []);

  const EMPLOYEES_FILTERS = useMemo<FilterRangeValue[]>(() => {
    return NON_LOCALIZED_EMPLOYEES_FILTERS.map((data) => ({
      ...data,
      label: t(
        `saas.search.filters.fields.details.employees.values.${data.stringValue}`,
      ),
    }));
  }, []);

  const OWNER_WORKED_HOURS_FILTERS = useMemo<FilterRangeValue[]>(() => {
    return NON_LOCALIZED_OWNER_WORKED_HOURS_FILTERS.map((data) => ({
      ...data,
      label: t(
        `saas.search.filters.fields.details.ownerWeeklyHours.values.${data.stringValue}`,
      ),
    }));
  }, []);

  function handleEstablishmentChange(filterValue: FilterRangeValue) {
    setFilters((prev) => ({
      ...prev,
      establishmentAgeRange: filterValue.value as RangeFilterType<number>,
    }));
  }

  function handleEmployeesChange(filterValue: FilterRangeValue) {
    setFilters((prev) => ({
      ...prev,
      employeeCountRange: filterValue.value as RangeFilterType<number>,
    }));
  }

  function handleOwnerWorkedHoursChange(filterValue: FilterRangeValue) {
    setFilters((prev) => ({
      ...prev,
      ownerWorkedHoursRange: filterValue.value as RangeFilterType<number>,
    }));
  }

  function handlePropertyIncludedChange(value: boolean) {
    setFilters((prev) => ({
      ...prev,
      propertyIncluded: value,
    }));
  }

  function toggleListedBy(target: "owner" | "broker") {
    setFilters((prev) => {
      const exists = prev.listedBy.includes(target);
      return {
        ...prev,
        listedBy: exists
          ? prev.listedBy.filter((value) => value !== target)
          : [...prev.listedBy, target],
      };
    });
  }

  function toggleSaleType(type: SaleTypeType) {
    setFilters((prev) => {
      const exists = prev.saleTypes.includes(type);
      return {
        ...prev,
        saleTypes: exists
          ? prev.saleTypes.filter((t) => t !== type)
          : [...prev.saleTypes, type],
      };
    });
  }

  function resetDetails() {
    setFilters((prev) => ({
      ...prev,
      establishmentAgeRange: { min: null, max: null },
      employeeCountRange: { min: null, max: null },
      ownerWorkedHoursRange: { min: null, max: null },
      propertyIncluded: null,
      listedBy: [],
      saleTypes: [],
    }));
  }

  const dc = facets.data?.detailsCounts;

  return (
    <div className="flex flex-col gap-4">
      <RangeFilter
        type="single"
        items={ESTABLISHMENT_FILTERS}
        defaultValue={ESTABLISHMENT_FILTERS.find(
          (item) =>
            item.value.min === filters.establishmentAgeRange?.min &&
            item.value.max === filters.establishmentAgeRange?.max,
        )}
        onChange={handleEstablishmentChange}
        counts={dc?.establishmentAge}
        countsLoading={facets.isLoading}
        leading={
          <FilterLabel hint={t("saas.search.filters.fields.details.establishment.hint")}>
            {t("saas.search.filters.fields.details.establishment.label")}
          </FilterLabel>
        }
      />

      <RangeFilter
        type="single"
        items={EMPLOYEES_FILTERS}
        defaultValue={EMPLOYEES_FILTERS.find(
          (item) =>
            item.value.min === filters.employeeCountRange?.min &&
            item.value.max === filters.employeeCountRange?.max,
        )}
        onChange={handleEmployeesChange}
        counts={dc?.employeeCount}
        countsLoading={facets.isLoading}
        leading={
          <FilterLabel hint={t("saas.search.filters.fields.details.employees.hint")}>
            {t("saas.search.filters.fields.details.employees.label")}
          </FilterLabel>
        }
      />

      <RangeFilter
        type="single"
        items={OWNER_WORKED_HOURS_FILTERS}
        defaultValue={OWNER_WORKED_HOURS_FILTERS.find(
          (item) =>
            item.value.min === filters.ownerWorkedHoursRange?.min &&
            item.value.max === filters.ownerWorkedHoursRange?.max,
        )}
        onChange={handleOwnerWorkedHoursChange}
        counts={dc?.ownerHours}
        countsLoading={facets.isLoading}
        leading={
          <FilterLabel hint={t("saas.search.filters.fields.details.ownerWeeklyHours.hint")}>
            {t("saas.search.filters.fields.details.ownerWeeklyHours.label")}
          </FilterLabel>
        }
      />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="propertyIncluded"
            checked={filters.propertyIncluded ?? false}
            onCheckedChange={handlePropertyIncludedChange}
            className="w-5 h-5 border-2 rounded-[6px]"
          />

          <FilterLabel
            htmlFor="propertyIncluded"
            hint={t("saas.search.filters.fields.details.propertyIncluded.hint")}
          >
            {t("saas.search.filters.fields.details.propertyIncluded.label")}
          </FilterLabel>
        </div>

        <span className="text-xs text-muted-foreground font-data tabular-nums">
          {facets.isLoading ? (
            <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
          ) : (
            dc?.propertyIncluded ?? 0
          )}
        </span>
      </div>

      <div className="space-y-2">
        <FilterLabel hint={t("saas.search.filters.fields.details.listedBy.hint")}>
          {t("saas.search.filters.fields.details.listedBy.label")}
        </FilterLabel>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="listedByOwner"
                checked={filters.listedBy.includes("owner")}
                onCheckedChange={() => toggleListedBy("owner")}
                className="w-5 h-5 border-2 rounded-[6px]"
              />
              <FilterLabel
                htmlFor="listedByOwner"
                hint={t("saas.search.filters.fields.details.listedBy.owner.hint")}
              >
                {t("saas.search.filters.fields.details.listedBy.owner.label")}
              </FilterLabel>
            </div>
            <span className="text-xs text-muted-foreground font-data tabular-nums">
              {facets.isLoading ? (
                <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
              ) : (
                dc?.listedBy?.owner ?? 0
              )}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="listedByBroker"
                checked={filters.listedBy.includes("broker")}
                onCheckedChange={() => toggleListedBy("broker")}
                className="w-5 h-5 border-2 rounded-[6px]"
              />
              <FilterLabel
                htmlFor="listedByBroker"
                hint={t("saas.search.filters.fields.details.listedBy.broker.hint")}
              >
                {t("saas.search.filters.fields.details.listedBy.broker.label")}
              </FilterLabel>
            </div>
            <span className="text-xs text-muted-foreground font-data tabular-nums">
              {facets.isLoading ? (
                <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
              ) : (
                dc?.listedBy?.broker ?? 0
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <FilterLabel hint={t("saas.search.filters.fields.details.saleType.hint")}>
          {t("saas.search.filters.fields.details.saleType.label")}
        </FilterLabel>
        <div className="flex flex-col gap-2">
          {SALE_TYPE_OPTIONS.map((type) => (
            <div key={type} className="flex items-center justify-between gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`saleType-${type}`}
                  checked={filters.saleTypes.includes(type)}
                  onCheckedChange={() => toggleSaleType(type)}
                  className="w-5 h-5 border-2 rounded-[6px]"
                />
                <FilterLabel
                  htmlFor={`saleType-${type}`}
                  hint={t(`saas.search.filters.fields.details.saleType.${type}.hint`)}
                >
                  {t(`saas.search.filters.fields.details.saleType.${type}.label`)}
                </FilterLabel>
              </div>
              <span className="text-xs text-muted-foreground font-data tabular-nums">
                {facets.isLoading ? (
                  <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
                ) : (
                  dc?.saleType?.[type] ?? 0
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between border-t pt-3">
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          onClick={resetDetails}
        >
          Reset
        </button>

        <span className="text-xs text-muted-foreground">
          {facets.isLoading ? (
            <span className="inline-block w-8 h-3 bg-muted animate-pulse rounded" />
          ) : (
            <span className="font-data tabular-nums text-foreground">
              {facets.data?.total ?? 0}
            </span>
          )}{" "}
          businesses match
        </span>
      </div>
    </div>
  );
}
